import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { classifyAxiosError } from '@/utils/axiosConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHED_USER_KEY = 'cached_user';

// Read the last-known user profile from localStorage so we can hydrate
// the app instantly when offline (avoids bouncing to /login on a flaky
// network).
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.id) return parsed;
    return null;
  } catch {
    return null;
  }
};

const writeCachedUser = (u) => {
  try {
    if (u && u.id) localStorage.setItem(CACHED_USER_KEY, JSON.stringify(u));
  } catch {
    /* quota exceeded — ignore */
  }
};

const clearCachedUser = () => {
  try { localStorage.removeItem(CACHED_USER_KEY); } catch { /* noop */ }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Hydrate user from localStorage cache immediately so the app can render
  // protected routes even when /auth/me hasn't returned (e.g. offline at boot).
  const [user, setUser] = useState(() => readCachedUser());
  // `loading` stays true only when we have NO cached user AND we are still
  // trying to verify a token. With cached data we can skip the splash screen.
  const [loading, setLoading] = useState(() => {
    const hasToken = !!localStorage.getItem('token');
    const hasCachedUser = !!readCachedUser();
    return hasToken && !hasCachedUser;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  // True only when we couldn't verify the token because of a network/timeout
  // problem (NOT because the token is invalid). When this is true we keep
  // the token in localStorage and auto-retry when connectivity returns.
  const [offlineSession, setOfflineSession] = useState(false);
  const verifyInFlight = useRef(false);

  /**
   * Try to fetch the current user with the saved token.
   *
   * Resolution rules:
   *  - 200 OK            → set user, refresh cache, clear offline flag
   *  - 401/403           → token is truly invalid → wipe it, force login
   *  - timeout / network → KEEP token + cached user, mark session offline,
   *                        wait for `online` event to retry. Never log the
   *                        user out for connectivity reasons.
   */
  const verifyToken = useCallback(async (silent = false) => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) {
      if (!silent) setLoading(false);
      return { ok: false, reason: 'no_token' };
    }
    if (verifyInFlight.current) return { ok: false, reason: 'in_flight' };
    verifyInFlight.current = true;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
        timeout: 15000,
      });
      setUser(response.data);
      writeCachedUser(response.data);
      setToken(savedToken);
      setOfflineSession(false);
      return { ok: true };
    } catch (error) {
      const c = classifyAxiosError(error);
      if (c.kind === 'http' && (c.status === 401 || c.status === 403)) {
        // Token is truly invalid → log out
        localStorage.removeItem('token');
        clearCachedUser();
        setToken(null);
        setUser(null);
        setOfflineSession(false);
        return { ok: false, reason: 'invalid_token' };
      }
      // Network / timeout / 5xx — keep the token + cached user, mark offline
      console.warn('verifyToken transient error:', c.kind, c.message);
      setOfflineSession(true);
      setToken(savedToken);
      // If we have a cached user, hydrate it so PrivateRoute lets us through
      const cached = readCachedUser();
      if (cached && !user) setUser(cached);
      return { ok: false, reason: 'offline', error: c };
    } finally {
      verifyInFlight.current = false;
      if (!silent) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    verifyToken();
    // Run only once on mount. Including `verifyToken` (or `user`) in deps
    // creates an infinite loop: setUser → new verifyToken callback → useEffect
    // re-runs → setUser again → ...  That loop was spamming the API with
    // /auth/me + /user/messages every <1s and causing flaky bell state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-recover the session when connectivity is restored.
  useEffect(() => {
    const handleOnline = () => {
      if (offlineSession && localStorage.getItem('token')) {
        verifyToken(true);
      }
    };
    window.addEventListener('online', handleOnline);
    // Also retry on tab focus — useful when the device wakes from sleep.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && offlineSession) {
        verifyToken(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [offlineSession, verifyToken]);

  const login = async (cedula, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { cedula, password },
        { timeout: 30000 } // 30s — generous for slow 3G/4G
      );

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return {
          success: false,
          requires2FA: true,
          tempToken: response.data.temp_token,
          emailHint: response.data.email_hint
        };
      }

      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      writeCachedUser(userData);
      setOfflineSession(false);
      return { success: true };
    } catch (error) {
      const c = classifyAxiosError(error);
      // Prefer backend message when available, fall back to network classifier
      const errorMsg =
        c.kind === 'http' && typeof c.detail === 'string'
          ? c.detail
          : c.message || 'Error al iniciar sesión';
      return {
        success: false,
        error: errorMsg,
        errorKind: c.kind, // 'timeout' | 'offline' | 'network' | 'http'
      };
    }
  };

  const loginWith2FA = (result) => {
    // Complete login after 2FA verification
    const { access_token, user: userData } = result;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    writeCachedUser(userData);
    setOfflineSession(false);
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData, {
        timeout: 30000,
      });

      // Check if it's a pending approval response (for revendedores or passport users)
      if (response.data.pending_approval) {
        return {
          success: false,
          pending_approval: true,
          pending_passport: response.data.pending_passport || false,
          message: response.data.message
        };
      }

      const { access_token, user: userDataResponse } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userDataResponse);
      writeCachedUser(userDataResponse);
      return { success: true };
    } catch (error) {
      const c = classifyAxiosError(error);
      const errorMsg =
        c.kind === 'http' && typeof c.detail === 'string'
          ? c.detail
          : c.message || 'Error al registrarse';
      return { success: false, error: errorMsg, errorKind: c.kind };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    clearCachedUser();
    setToken(null);
    setUser(null);
    setOfflineSession(false);
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setUser(response.data);
      writeCachedUser(response.data);
      setOfflineSession(false);
    } catch (error) {
      const c = classifyAxiosError(error);
      if (c.kind === 'http' && (c.status === 401 || c.status === 403)) {
        // Token actually invalid — log out
        logout();
      } else {
        // Network blip — silent
        console.warn('refreshUser transient error:', c.kind);
      }
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API}/auth/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      writeCachedUser(response.data);
      return { success: true };
    } catch (error) {
      const c = classifyAxiosError(error);
      return {
        success: false,
        error:
          c.kind === 'http' && typeof c.detail === 'string'
            ? c.detail
            : c.message || 'Error al actualizar perfil',
      };
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.put(
        `${API}/auth/change-password`,
        { old_password: oldPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { success: true };
    } catch (error) {
      const c = classifyAxiosError(error);
      return {
        success: false,
        error:
          c.kind === 'http' && typeof c.detail === 'string'
            ? c.detail
            : c.message || 'Error al cambiar contraseña',
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    offlineSession,
    login,
    loginWith2FA,
    register,
    logout,
    refreshUser,
    updateProfile,
    changePassword
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
};
